/* ═══ TEMA D — Producție: cardul liniei și fereastra liniei (30 sep 2026) ══════
 *
 * Dan, 30 sep: „Da" la bucata 3 — pe tema D cardul de linie arată ca în macheta
 * aprobată (poză, stare, 3 cifre mari, benzi colorate DOAR la probleme, un singur
 * buton „Deschide linia"), iar tot ce era înghesuit pe card — utilajele, scorul,
 * cele 4 butoane, restul cifrelor — se mută în fereastra liniei, la o atingere.
 *
 * Datele sunt aceleași ca pe tema de azi (`/api/lines/panel`, un singur apel);
 * acțiunile trec prin același `wireLinePanelContainer` (lucrări, alarme, AI,
 * detalii, marcaje, semnale), deci nu există două drumuri pentru același lucru.
 * Tema de azi nu trece pe aici: `overview.ts` alege constructorul după `temaD()`.
 */
import { api, escHtml } from './core';
import { type PanelLine, type PanelData, healthDonutSvg, wireLinePanelContainer } from './line-panel';
import { ic } from './tema';

type Masina = PanelLine['machines'][number] & { overdue?: number; next_due_hours?: number | null; next_due_action?: string | null;
    /** 1 nov 2026: textul stării din sursa unică */ next_due_text?: string | null };

const _linii = new Map<string, PanelLine>();
const _poze = new Map<string, string | null>();   // machine_id → miniatura (null = fără poză)

const TIP: Record<string, string> = {
    injection: 'Injecție', compression: 'Compresie', mold: 'Matriță', chiller: 'Răcitor', cooler: 'Răcitor',
    compressor: 'Compresor', dryer: 'Uscător', reservoir: 'Rezervor', control: 'Control', inspection: 'Inspecție',
    tampoprint: 'Tampografie', auxiliary: 'Auxiliar', robot: 'Robot', printer: 'Imprimare',
};
const IC_TIP: Record<string, string> = {
    injection: 'factory', compression: 'factory', mold: 'mold', chiller: 'snow', cooler: 'snow', compressor: 'wind',
    dryer: 'flame', reservoir: 'drop', control: 'monitor', inspection: 'eye', tampoprint: 'printer', robot: 'robot', printer: 'printer',
};

const _n = (v: number, zec = 0) => v.toLocaleString('ro-RO', { minimumFractionDigits: zec, maximumFractionDigits: zec });

interface Cifra { cheie: string; et: string; val: string; um: string; titlu?: string }

function _cifre(l: PanelLine): Cifra[] {
    const p = l.power, pr = l.productivity;
    const out: Cifra[] = [];
    out.push({ cheie: 'putere', et: 'Putere', val: p.kw_live != null ? _n(p.kw_live, 1) : '—', um: p.kw_live != null ? 'kW' : '',
        titlu: p.kw_avg_1h != null ? `Medie pe ultima oră: ${_n(p.kw_avg_1h, 1)} kW` : 'Consumul liniei, acum' });
    if (pr.bph_final != null) out.push({ cheie: 'ritm', et: 'Ritm', val: _n(pr.bph_final), um: '/h', titlu: 'Bucăți pe oră, produs final' });
    if (pr.caps_today != null) out.push({ cheie: 'capace', et: 'Capace azi', val: _n(pr.caps_today), um: pr.caps_today_partial ? '≈' : '',
        titlu: pr.caps_today_partial ? 'Parțial — date de la repornirea colectoarelor azi' : 'Capace bune de la miezul nopții' });
    if (l.main_machine_hours > 0) out.push({ cheie: 'ore', et: 'Ore', val: _n(l.main_machine_hours), um: '', titlu: 'Ore de funcționare, mașina principală' });
    if (l.mold && l.mold.cycles) out.push({ cheie: 'matrita', et: 'Matriță', val: _n(l.mold.cycles), um: '', titlu: `Cicluri pe matrița activă ${l.mold.id}` });
    out.push({ cheie: 'energie', et: 'Energie azi', val: _n(p.kwh_today, 1), um: 'kWh',
        titlu: p.kwh_partial ? 'Parțial — colectarea a avut pauze azi' : 'Energie de la miezul nopții' });
    if (l.oee && l.oee.oee != null) out.push({ cheie: 'oee', et: 'OEE', val: _n(l.oee.oee), um: '%',
        titlu: `Disponibilitate ${l.oee.availability ?? '—'}% × Performanță ${l.oee.performance ?? '—'}% × Calitate ${l.oee.quality ?? '—'}%` });
    if (pr.scrap_pct != null) out.push({ cheie: 'rebut', et: 'Rebut', val: _n(pr.scrap_pct, 1), um: '%', titlu: 'Mașină vs produs final, acum' });
    out.push({ cheie: 'alarme24', et: 'Alarme 24 h', val: _n(l.alarms.last24h || 0), um: '' });
    return out;
}

/** Cele 3 cifre ale cardului: puterea, apoi (în producție) ritmul și capacele, apoi ce există. */
function _cifreCard(l: PanelLine): Cifra[] {
    const toate = _cifre(l);
    const dupa = (k: string) => toate.find(c => c.cheie === k);
    const ordine = l.status === 'running'
        ? ['putere', 'ritm', 'capace', 'ore', 'matrita', 'energie', 'alarme24']
        : ['putere', 'ore', 'matrita', 'energie', 'alarme24'];
    return ordine.map(dupa).filter(Boolean).slice(0, 3) as Cifra[];
}

function _cifraHtml(c: Cifra): string {
    return `<div class="dl-cifra"${c.titlu ? ` title="${escHtml(c.titlu)}"` : ''}>
        <div class="dl-cifra-et">${escHtml(c.et)}</div>
        <div class="dl-cifra-val">${escHtml(c.val)}${c.um ? `<small>${escHtml(c.um)}</small>` : ''}</div>
    </div>`;
}

function _stare(l: PanelLine): string {
    if (!l.is_active) return `<span class="dl-stare dl-stare-neu">${ic('stop')}Inactivă</span>`;
    if (l.status === 'running') return `<span class="dl-stare dl-stare-ok">${ic('run')}În producție</span>`;
    if (l.status === 'stopped') return `<span class="dl-stare dl-stare-neu">${ic('stop')}Oprită</span>`;
    return `<span class="dl-stare dl-stare-neu">${ic('info')}Fără date</span>`;
}

function _foto(mid: string | null, tip: string | null, cls: string): string {
    const src = mid ? _poze.get(mid) : null;
    return `<span class="${cls}"${mid ? ` data-foto-mid="${escHtml(mid)}"` : ''}>${src ? `<img src="${escHtml(src)}" alt="" loading="lazy">` : ic(IC_TIP[tip || ''] || 'factory')}</span>`;
}

/** Benzile colorate — doar ce nu e în regulă: semnalul dominant, restanțele, marcajele de oprire. */
function _benzi(l: PanelLine): string {
    const sig = l.dominant_signal as ({ level: string; text: string; kind?: string } | null);
    const idsMasini = (l.machines || []).map(m => m.id).join(',');
    const banda = (nivel: string, icon: string, text: string, kind: string) =>
        `<button type="button" class="dl-banda dl-banda-${nivel}" data-act="signal" data-kind="${escHtml(kind)}" data-line="${escHtml(l.id)}" data-masini="${escHtml(idsMasini)}">${ic(icon)}<span>${escHtml(text)}</span>${ic('chev', 'dl-banda-go')}</button>`;
    let html = '';
    if (sig) html += banda(sig.level === 'crit' ? 'crit' : 'warn', sig.level === 'crit' ? 'octagon' : (sig.kind === 'overdue' ? 'clock' : 'warn'), sig.text, sig.kind || '');
    if (l.health.overdue && (!sig || sig.kind !== 'overdue')) {
        const n = l.health.overdue;
        html += banda('warn', 'clock', `${n} ${n === 1 ? 'lucrare depășită' : 'lucrări depășite'}`, 'overdue');
    }
    (l.mentenanta_marcata || []).forEach(mk => {
        const produce = l.status === 'running';
        const din = (mk.din || '').replace('T', ' ').slice(0, 16);
        html += `<div class="dl-banda dl-banda-${produce ? 'crit' : 'warn'} dl-marcaj">${ic('wrench')}
            <span><b>${escHtml(mk.nume)}</b> e marcat „oprit pentru mentenanță"${din ? ` din ${escHtml(din)}` : ''}${produce ? ' — dar linia produce' : ''}${mk.motiv ? ` (${escHtml(mk.motiv)})` : ''}</span>
            <button type="button" class="dl-marcaj-btn" data-act="clear-stop" data-machine="${escHtml(mk.machine_id)}">Scoate marcajul</button>
        </div>`;
    });
    return html;
}

/** Cardul liniei pe tema D. */
export function buildLineCardD(l: PanelLine, opts: { admin?: boolean } = {}): string {
    _linii.set(l.id, l);
    const sig = l.dominant_signal;
    const tip = (l.line_type || '').toLowerCase();
    const sub = [l.product, tip && tip !== 'utilitate' ? tip : ''].filter(Boolean).join(' · ');
    const mainTip = (l.machines.find(m => m.id === l.main_machine) || l.machines[0])?.type || null;
    return `<div class="dash-line-card lp-card dl-card${sig && sig.level === 'crit' ? ' dl-crit' : ''}${!l.is_active ? ' dl-inactiva' : ''}"
            data-line="${escHtml(l.id)}" data-product="${escHtml(l.product || '')}" role="button" tabindex="0"
            aria-label="Linia ${escHtml(l.id)} — deschide">
        <div class="dl-cap">
            ${_foto(l.main_machine, mainTip, 'dl-foto')}
            <div class="dl-nume"><div class="dl-id">${escHtml(l.id)}</div>${sub ? `<div class="dl-sub">${escHtml(sub)}</div>` : ''}</div>
            ${_stare(l)}
        </div>
        <div class="dl-cifre">${_cifreCard(l).map(_cifraHtml).join('')}</div>
        ${_benzi(l)}
        ${opts.admin
            ? `<div class="dl-jos"><button type="button" class="dl-deschide" data-act="linie-d" data-line="${escHtml(l.id)}">Deschide linia ${escHtml(l.id)}${ic('chev')}</button>
               <button type="button" class="dl-deschide dl-config" data-act="config" data-line="${escHtml(l.id)}">${ic('settings')}Configurare</button></div>`
            : `<button type="button" class="dl-deschide" data-act="linie-d" data-line="${escHtml(l.id)}">Deschide linia ${escHtml(l.id)}${ic('chev')}</button>`}
    </div>`;
}

/** Cardul utilităților pe tema D: o listă simplă (nume, putere, restanțe), nu 15 buline. */
export function buildUtilitiesCardD(uc: PanelData['utilities_card']): string {
    const vizibile = uc.entries.filter(e => e.id !== 'TRANSFORMATOR');
    const totalRest = vizibile.reduce((s, e) => s + (e.overdue || 0), 0);
    const randuri = vizibile.map(e => {
        const merge = e.status === 'running';
        const rest = e.overdue || 0;
        return `<button type="button" class="lp-chip dl-util" data-machine="${escHtml(e.id)}" data-mname="${escHtml(e.id)}"
                title="${escHtml(e.serves.length ? 'Deservește: ' + e.serves.join(', ') : '')}">
            <span class="dl-punct ${merge ? 'dl-punct-ok' : ''}" aria-label="${merge ? 'pornit' : 'oprit'}"></span>
            <span class="dl-util-nume">${escHtml(e.id.replace(/_/g, ' '))}</span>
            ${rest ? `<span class="dl-insigna dl-insigna-warn">${ic('clock')}${rest}</span>` : ''}
            <span class="dl-util-kw">${e.kw != null ? `${_n(e.kw, 1)} <small>kW</small>` : '—'}</span>
            ${ic('chev', 'dl-go')}
        </button>`;
    }).join('');
    return `<div class="dash-line-card lp-card dl-card dl-card-util" data-line="_utils">
        <div class="dl-cap">
            <span class="dl-foto">${ic('snow')}</span>
            <div class="dl-nume"><div class="dl-id">Utilități</div><div class="dl-sub">transformator · răcitoare · aer comprimat</div></div>
        </div>
        <div class="dl-cifre">
            <div class="dl-cifra" title="Puterea totală a fabricii (contorul transformatorului)"><div class="dl-cifra-et">Total fabrică</div>
                <div class="dl-cifra-val">${uc.total_factory_kw != null ? _n(uc.total_factory_kw, 1) : '—'}<small>kW</small></div></div>
            <div class="dl-cifra"><div class="dl-cifra-et">Pornite</div>
                <div class="dl-cifra-val">${vizibile.filter(e => e.status === 'running').length}<small>/ ${vizibile.length}</small></div></div>
            <div class="dl-cifra"><div class="dl-cifra-et">Depășite</div>
                <div class="dl-cifra-val">${totalRest}</div></div>
        </div>
        <div class="dl-util-lista">${randuri}</div>
    </div>`;
}

/** Antetul Producției pe D: câte linii merg / stau + „Toate liniile" / „Cer atenție". */
export function antetProductieD(pd: PanelData, filtru: string): string {
    const active = pd.lines.filter(l => l.is_active);
    const merg = active.filter(l => l.status === 'running').length;
    const oprite = active.filter(l => l.status !== 'running').length;
    const inactive = pd.lines.length - active.length;
    const atentie = pd.lines.filter(l => l.dominant_signal || (l.alarms.critical + l.alarms.warning) > 0 || l.health.overdue > 0).length;
    return `<div class="dd-sumar">
            <span class="dd-chip dd-chip-ok">${ic('run')}${merg} în producție</span>
            <span class="dd-chip">${ic('stop')}${oprite} oprite</span>
            ${inactive ? `<span class="dd-chip">${inactive} inactive</span>` : ''}
        </div>
        <div class="dd-seg" role="group" aria-label="Ce linii arăt">
            <button type="button" data-dfiltru="" aria-pressed="${filtru === '' ? 'true' : 'false'}">Toate liniile</button>
            <button type="button" data-dfiltru="alert" aria-pressed="${filtru === 'alert' ? 'true' : 'false'}">Cer atenție <b>${atentie}</b></button>
        </div>`;
}

/** Pozele reale ale utilajelor, încărcate o singură dată (miniaturi de 320 px). */
export async function incarcaPozeD(gazda: ParentNode): Promise<void> {
    const locuri = Array.from(gazda.querySelectorAll<HTMLElement>('[data-foto-mid]'));
    const lipsa = [...new Set(locuri.map(e => e.dataset.fotoMid || '').filter(id => id && !_poze.has(id)))];
    for (let i = 0; i < lipsa.length; i += 90) {
        const bucata = lipsa.slice(i, i + 90);
        try {
            const r = await api('/api/config/machines/photos_batch?ids=' + encodeURIComponent(bucata.join(','))) || {};
            bucata.forEach(id => {
                const lista = (r[id] || []) as Array<{ thumb_url?: string; url: string; photo_type?: string }>;
                const p = lista.find(x => x.photo_type === 'general') || lista[0];
                _poze.set(id, p ? (p.thumb_url || p.url) : null);
            });
        } catch (_e) { bucata.forEach(id => _poze.set(id, null)); }
    }
    locuri.forEach(e => {
        const src = _poze.get(e.dataset.fotoMid || '');
        if (src && !e.querySelector('img')) e.innerHTML = `<img src="${escHtml(src)}" alt="" loading="lazy">`;
    });
}

/* ── Fereastra liniei ───────────────────────────────────────────────────── */
function _inchideFereastra(): void {
    document.getElementById('d-linie-overlay')?.remove();
}

export function deschideLinieD(lineId: string): void {
    const l = _linii.get(lineId);
    if (!l) {   // n-a trecut încă prin Producție (ex. deschisă din altă parte) — drumul vechi
        (window as any).openLineLayoutDetail?.(lineId, '');
        return;
    }
    _inchideFereastra();
    const h = l.health;
    const masini = (l.machines || []) as Masina[];
    const mainTip = (masini.find(m => m.id === l.main_machine) || masini[0])?.type || null;
    const sub = [l.product, (l.line_type || '').toLowerCase(), `${masini.length} utilaje`].filter(Boolean).join(' · ');
    const pr = l.productivity;
    const sursa: Record<string, string> = { sfm: 'produs final (SFM)', foldare: 'numărat la cutie', machine: 'mașină', nominal: 'estimat' };

    const randMasina = (m: Masina) => {
        const rest = m.overdue || 0;
        const urm = !rest && m.next_due_text ? `urm.: ${m.next_due_action ? m.next_due_action + ' — ' : ''}${m.next_due_text}`
            : m.next_due_hours != null && !rest ? `urm. în ${_n(m.next_due_hours)} h${m.next_due_action ? ' — ' + m.next_due_action : ''}` : '';
        return `<button type="button" class="lp-chip dlf-utilaj" data-machine="${escHtml(m.id)}" data-mname="${escHtml(m.name)}">
            ${_foto(m.id, m.type, 'dlf-poza')}
            <span class="dlf-u-text">
                <span class="dlf-u-nume">${escHtml(m.name)}${m.id === l.main_machine ? ' <span class="dlf-principal">principală</span>' : ''}</span>
                <span class="dlf-u-sub">${escHtml(TIP[m.type] || m.type || '')}${urm ? ` · ${escHtml(urm)}` : ''}</span>
            </span>
            <span class="dlf-u-dreapta">
                ${rest ? `<span class="dl-insigna dl-insigna-warn">${ic('clock')}${rest} ${rest === 1 ? 'restantă' : 'restante'}</span>` : ''}
                ${m.alarms ? `<span class="dl-insigna dl-insigna-crit">${ic('bell')}${m.alarms}</span>` : ''}
                ${m.kw != null ? `<span class="dlf-u-kw">${_n(m.kw, 1)} <small>kW</small></span>` : ''}
            </span>
            ${ic('chev', 'dl-go')}
        </button>`;
    };
    const utilitati = (l.utilities || []).map(u => `<div class="dlf-util">
            <span class="dl-punct ${u.status === 'running' ? 'dl-punct-ok' : ''}"></span>
            <span>${escHtml(u.id.replace(/_/g, ' '))}</span>
            <span class="dlf-util-kw">${u.kw != null ? `${_n(u.kw, 1)} kW` : (u.status === 'running' ? 'pornit' : 'oprit')}</span>
        </div>`).join('');

    const ov = document.createElement('div');
    ov.id = 'd-linie-overlay';
    ov.className = 'proc-overlay dlf-ov';
    ov.innerHTML = `<div class="dlf-panou" role="dialog" aria-modal="true" aria-label="Linia ${escHtml(l.id)}">
        <div class="dlf-antet">
            ${_foto(l.main_machine, mainTip, 'dl-foto dlf-foto')}
            <div class="dl-nume"><div class="dl-id">Linia ${escHtml(l.id)}</div><div class="dl-sub">${escHtml(sub)}</div><div class="dlf-stare">${_stare(l)}</div></div>
            <button type="button" class="dlf-x" aria-label="Închide">${ic('x')}</button>
        </div>
        <div class="dlf-corp">
            ${_benzi(l)}
            <div class="dl-cifre dlf-cifre">${_cifre(l).map(_cifraHtml).join('')}</div>
            ${pr.bph_final != null && sursa[pr.source] ? `<div class="dlf-nota">Ritmul vine de la: ${escHtml(sursa[pr.source])}${pr.source_machine ? ` (${escHtml(pr.source_machine)})` : ''}</div>` : ''}
            <div class="dlf-sanatate">
                ${healthDonutSvg(h.pct, 64)}
                <div>
                    <div class="dlf-s-titlu">Scor de sănătate${h.pct != null ? ` ${h.pct}` : ' — fără date'}</div>
                    <div class="dlf-s-sub">${h.overdue ? `${h.overdue} ${h.overdue === 1 ? 'lucrare depășită' : 'lucrări depășite'}` : 'nicio lucrare depășită'}${h.next_due_text ? ` · următoarea${h.next_due_machine ? ` pe ${escHtml(h.next_due_machine)}` : ''}${h.next_due_action ? `: ${escHtml(h.next_due_action)}` : ''} — ${escHtml(h.next_due_text)}` : h.next_due_hours != null ? ` · următoarea în ${_n(h.next_due_hours)} h${h.next_due_machine ? ` pe ${escHtml(h.next_due_machine)}` : ''}${h.next_due_action ? `: ${escHtml(h.next_due_action)}` : ''}` : ''}</div>
                </div>
            </div>
            <div class="dlf-actiuni">
                <button type="button" class="dlf-act dlf-act-principal" data-act="tasks" data-line="${escHtml(l.id)}">${ic('wrench')}Lucrările liniei</button>
                <button type="button" class="dlf-act" data-act="urmeaza" data-line="${escHtml(l.id)}">${ic('calendar')}Ce urmează</button>
                <button type="button" class="dlf-act" data-act="alarms" data-line="${escHtml(l.id)}">${ic('bell')}Alarme${(l.alarms.critical + l.alarms.warning) ? ` <b>${l.alarms.critical + l.alarms.warning}</b>` : ''}</button>
                <button type="button" class="dlf-act" data-act="ai" data-line="${escHtml(l.id)}" data-main="${escHtml(l.main_machine || '')}">${ic('chat')}Întreabă AI</button>
                <button type="button" class="dlf-act" data-act="detail" data-line="${escHtml(l.id)}" data-product="${escHtml(l.product || '')}">${ic('map')}Schema liniei</button>
            </div>
            <h3 class="dlf-h">Utilajele liniei</h3>
            <div class="dlf-utilaje">${masini.map(randMasina).join('') || '<div class="dlf-gol">Nicio mașină pe această linie.</div>'}</div>
            ${utilitati ? `<h3 class="dlf-h">Utilități care o deservesc</h3><div class="dlf-utilitati">${utilitati}</div>` : ''}
        </div>
    </div>`;
    document.body.appendChild(ov);
    const panou = ov.querySelector('.dlf-panou') as HTMLElement;
    // aceleași acțiuni ca pe cardul de azi (lucrări, alarme, AI, schemă, semnale, marcaje, utilaje)
    wireLinePanelContainer(panou);
    // o acțiune care duce în altă parte închide fereastra (scoaterea marcajului rămâne aici).
    // În faza de CAPTURĂ: `wireLinePanelContainer` oprește propagarea clicului pe acțiuni.
    ov.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        if (t === ov || t.closest('.dlf-x')) { _inchideFereastra(); return; }
        const a = t.closest('[data-act], .lp-chip') as HTMLElement | null;
        if (a && a.dataset.act !== 'clear-stop') setTimeout(_inchideFereastra, 0);
    }, true);
    incarcaPozeD(ov);
    (ov.querySelector('.dlf-x') as HTMLElement | null)?.focus();
}
(window as any).deschideLinieD = deschideLinieD;
